// House hub navigation and easter egg logic
import { navigateTo } from './router';
import { glitch } from './effects';
import { prefersReducedMotion } from './animations';

// Easter egg trigger words and their destinations
const EASTER_EGG_WORDS: Record<string, string> = {
  wesleyan: 'basement',
  vectors: 'archive',
  basement: 'basement',
  attic: 'attic',
  archive: 'archive',
  chopra: 'attic',
  carlos: 'basement',
  manuscript: 'archive',
  market: 'market',
  crypt: 'crypt',
  forbidden: 'market',
  edition: 'market',
  visitors: 'crypt',
  checked: 'crypt',
};

// Special key sequences (Konami-style)
const SPECIAL_SEQUENCES: Record<string, string> = {
  'ArrowUp,ArrowUp,ArrowDown,ArrowDown,ArrowLeft,ArrowRight,ArrowLeft,ArrowRight': 'archive',
  'Escape,Escape,Escape': 'attic',
};

// Current typed sequence for easter egg detection
let typedSequence = '';
let keySequence: string[] = [];

// Discovery tracking storage key
const DISCOVERY_STORAGE_KEY = 'chopras-discoveries';

// Discovery state
interface DiscoveryState {
  basement: boolean;
  attic: boolean;
  archive: boolean;
  market: boolean;
  crypt: boolean;
  discoveryMethods: Record<string, string[]>;
  firstVisit: number;
  totalDiscoveries: number;
}

let discoveryState: DiscoveryState = {
  basement: false,
  attic: false,
  archive: false,
  market: false,
  crypt: false,
  discoveryMethods: {},
  firstVisit: Date.now(),
  totalDiscoveries: 0,
};

/**
 * Initialize house hub interactions
 */
export function initHouseHub(container: HTMLElement): void {
  // Load discovery state from localStorage
  loadDiscoveryState();

  // Setup corridor navigation
  setupCorridorNavigation(container);

  // Setup easter egg detection (typed words)
  setupEasterEggDetection();

  // Setup special key sequence detection
  setupKeySequenceDetection();

  // Setup hidden clickable areas
  setupHiddenAreas(container);

  // Setup idle timer for easter eggs
  setupIdleTimer();

  // Setup console easter eggs
  setupConsoleEasterEggs();

  // Update visual state based on discoveries
  updateDiscoveryVisuals(container);

  // Log view-source hint to console
  logViewSourceHint();
}

/**
 * Setup click handlers for corridor navigation with arrow key support
 */
function setupCorridorNavigation(container: HTMLElement): void {
  const corridors = container.querySelectorAll('[data-corridor]') as NodeListOf<HTMLElement>;
  const corridorsArray = Array.from(corridors);

  corridors.forEach((corridor) => {
    corridor.addEventListener('click', () => {
      const target = corridor.getAttribute('data-corridor');
      if (target) {
        navigateTo(target);
      }
    });

    // Keyboard accessibility with arrow key navigation
    corridor.addEventListener('keydown', (e: Event) => {
      const keyEvent = e as KeyboardEvent;

      // Enter or Space to activate
      if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
        keyEvent.preventDefault();
        const target = corridor.getAttribute('data-corridor');
        if (target) {
          navigateTo(target);
        }
        return;
      }

      // Arrow key navigation between corridors
      const currentRow = corridor.closest('.house-corridors-row');
      const isTopRow = currentRow?.classList.contains('house-corridors-row--top');
      const isBottomRow = currentRow?.classList.contains('house-corridors-row--bottom');

      // Get siblings in current row
      const rowCorridors = currentRow
        ? Array.from(currentRow.querySelectorAll('[data-corridor]')) as HTMLElement[]
        : [];
      const rowIndex = rowCorridors.indexOf(corridor);

      switch (keyEvent.key) {
        case 'ArrowRight':
          keyEvent.preventDefault();
          // Move to next in row, or wrap to first
          if (rowIndex < rowCorridors.length - 1) {
            rowCorridors[rowIndex + 1].focus();
          } else if (rowCorridors.length > 0) {
            rowCorridors[0].focus();
          }
          break;

        case 'ArrowLeft':
          keyEvent.preventDefault();
          // Move to previous in row, or wrap to last
          if (rowIndex > 0) {
            rowCorridors[rowIndex - 1].focus();
          } else if (rowCorridors.length > 0) {
            rowCorridors[rowCorridors.length - 1].focus();
          }
          break;

        case 'ArrowDown':
          keyEvent.preventDefault();
          // Move to bottom row, or wrap to top
          if (isTopRow) {
            const bottomRow = container.querySelector('.house-corridors-row--bottom');
            const bottomCorridors = bottomRow?.querySelectorAll('[data-corridor]');
            if (bottomCorridors && bottomCorridors.length > 0) {
              // Try to maintain relative position
              const targetIndex = Math.min(rowIndex, bottomCorridors.length - 1);
              (bottomCorridors[targetIndex] as HTMLElement).focus();
            }
          } else if (isBottomRow) {
            const topRow = container.querySelector('.house-corridors-row--top');
            const topCorridors = topRow?.querySelectorAll('[data-corridor]');
            if (topCorridors && topCorridors.length > 0) {
              const targetIndex = Math.min(rowIndex, topCorridors.length - 1);
              (topCorridors[targetIndex] as HTMLElement).focus();
            }
          }
          break;

        case 'ArrowUp':
          keyEvent.preventDefault();
          // Move to top row, or wrap to bottom
          if (isBottomRow) {
            const topRow = container.querySelector('.house-corridors-row--top');
            const topCorridors = topRow?.querySelectorAll('[data-corridor]');
            if (topCorridors && topCorridors.length > 0) {
              const targetIndex = Math.min(rowIndex, topCorridors.length - 1);
              (topCorridors[targetIndex] as HTMLElement).focus();
            }
          } else if (isTopRow) {
            const bottomRow = container.querySelector('.house-corridors-row--bottom');
            const bottomCorridors = bottomRow?.querySelectorAll('[data-corridor]');
            if (bottomCorridors && bottomCorridors.length > 0) {
              const targetIndex = Math.min(rowIndex, bottomCorridors.length - 1);
              (bottomCorridors[targetIndex] as HTMLElement).focus();
            }
          }
          break;

        case 'Home':
          keyEvent.preventDefault();
          // Focus first corridor
          if (corridorsArray.length > 0) {
            corridorsArray[0].focus();
          }
          break;

        case 'End':
          keyEvent.preventDefault();
          // Focus last corridor
          if (corridorsArray.length > 0) {
            corridorsArray[corridorsArray.length - 1].focus();
          }
          break;
      }
    });
  });
}

/**
 * Listen for typed easter egg keywords
 */
function setupEasterEggDetection(): void {
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key.length === 1) {
      typedSequence += e.key.toLowerCase();

      // Keep only last 20 characters
      if (typedSequence.length > 20) {
        typedSequence = typedSequence.slice(-20);
      }

      // Check for easter egg words
      for (const word of Object.keys(EASTER_EGG_WORDS)) {
        if (typedSequence.includes(word)) {
          triggerEasterEgg(word, 'typed');
          typedSequence = '';
          break;
        }
      }
    }
  };

  document.addEventListener('keydown', handleKeydown);
  keydownHandler = handleKeydown;
}

// Store handler reference for cleanup
let keydownHandler: ((e: KeyboardEvent) => void) | null = null;
let keySequenceHandler: ((e: KeyboardEvent) => void) | null = null;

/**
 * Listen for special key sequences (Konami-style)
 */
function setupKeySequenceDetection(): void {
  const handleKeySequence = (e: KeyboardEvent) => {
    // Only track special keys for sequences
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Escape'].includes(e.key)) {
      keySequence.push(e.key);

      // Keep only last 10 keys
      if (keySequence.length > 10) {
        keySequence = keySequence.slice(-10);
      }

      // Check for special sequences
      const sequenceStr = keySequence.join(',');
      for (const [sequence, destination] of Object.entries(SPECIAL_SEQUENCES)) {
        if (sequenceStr.endsWith(sequence)) {
          triggerEasterEgg(sequence, 'sequence');
          keySequence = [];
          navigateTo(destination);
          break;
        }
      }
    }
  };

  document.addEventListener('keydown', handleKeySequence);
  keySequenceHandler = handleKeySequence;
}

/**
 * Handle easter egg trigger
 */
function triggerEasterEgg(trigger: string, method: string): void {
  const destination = EASTER_EGG_WORDS[trigger];
  if (!destination) return;

  // Record discovery
  recordDiscovery(destination, method, trigger);

  // Show discovery feedback
  showDiscoveryFeedback(destination, method);

  // Navigate after brief delay for feedback
  setTimeout(() => {
    navigateTo(destination);
  }, 500);
}

/**
 * Load discovery state from localStorage
 */
function loadDiscoveryState(): void {
  try {
    const stored = localStorage.getItem(DISCOVERY_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      discoveryState = { ...discoveryState, ...parsed };
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Save discovery state to localStorage
 */
function saveDiscoveryState(): void {
  try {
    localStorage.setItem(DISCOVERY_STORAGE_KEY, JSON.stringify(discoveryState));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Record a discovery
 */
function recordDiscovery(location: string, method: string, trigger: string): void {
  const locationKey = location as keyof Pick<DiscoveryState, 'basement' | 'attic' | 'archive' | 'market' | 'crypt'>;

  // Check if this is a new discovery
  const isNewDiscovery = !discoveryState[locationKey];

  // Mark as discovered
  discoveryState[locationKey] = true;

  // Track discovery method
  if (!discoveryState.discoveryMethods[location]) {
    discoveryState.discoveryMethods[location] = [];
  }
  const methodKey = `${method}:${trigger}`;
  if (!discoveryState.discoveryMethods[location].includes(methodKey)) {
    discoveryState.discoveryMethods[location].push(methodKey);
  }

  // Update total count
  if (isNewDiscovery) {
    discoveryState.totalDiscoveries++;
  }

  // Save to localStorage
  saveDiscoveryState();

  // Log to console for view-source hunters
  if (isNewDiscovery) {
    console.log(
      `%c[DISCOVERY] %c${location.toUpperCase()} unlocked via ${method}`,
      'color: #8b0000; font-weight: bold',
      'color: #666'
    );
  }
}

/**
 * Announce a message to screen readers via the house status live region
 */
function announceToScreenReader(message: string): void {
  // Use the existing status live region or create a new one
  let srAnnouncer = document.querySelector('.house-sr-announcer') as HTMLElement;

  if (!srAnnouncer) {
    srAnnouncer = document.createElement('div');
    srAnnouncer.className = 'sr-only house-sr-announcer';
    srAnnouncer.setAttribute('role', 'status');
    srAnnouncer.setAttribute('aria-live', 'assertive');
    srAnnouncer.setAttribute('aria-atomic', 'true');
    document.body.appendChild(srAnnouncer);
  }

  srAnnouncer.textContent = message;

  // Clear after a delay to allow re-announcement
  setTimeout(() => {
    if (srAnnouncer.textContent === message) {
      srAnnouncer.textContent = '';
    }
  }, 1000);
}

/**
 * Show visual feedback when an easter egg is discovered
 */
function showDiscoveryFeedback(location: string, _method: string): void {
  const reducedMotion = prefersReducedMotion();

  // Announce to screen readers
  const locationNames: Record<string, string> = {
    basement: 'the Basement - hidden room discovered',
    attic: 'the Attic - hidden room discovered',
    archive: 'the Archive - hidden room discovered',
    market: 'the Market - hidden room discovered',
    crypt: 'the Crypt - hidden room discovered',
  };
  announceToScreenReader(`Accessing ${locationNames[location] || location}`);

  // Create feedback overlay
  const feedback = document.createElement('div');
  feedback.className = 'easter-egg-feedback';
  feedback.innerHTML = `
    <div class="easter-egg-feedback-inner">
      <span class="easter-egg-feedback-text">> ACCESSING ${location.toUpperCase()}...</span>
    </div>
  `;

  // Add inline styles for the feedback
  feedback.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    ${reducedMotion ? 'opacity: 1;' : 'animation: feedbackFlash 0.5s ease-out forwards;'}
  `;

  const inner = feedback.querySelector('.easter-egg-feedback-inner') as HTMLElement;
  if (inner) {
    inner.style.cssText = `
      font-family: 'IBM Plex Mono', monospace;
      font-size: 1.25rem;
      color: var(--red, #8b0000);
      text-shadow: 0 0 10px var(--red-glow, rgba(139, 0, 0, 0.5));
    `;
  }

  // Add animation keyframes if not already present (only needed without reduced motion)
  if (!reducedMotion && !document.querySelector('#easter-egg-styles')) {
    const style = document.createElement('style');
    style.id = 'easter-egg-styles';
    style.textContent = `
      @keyframes feedbackFlash {
        0% { opacity: 0; }
        20% { opacity: 1; }
        80% { opacity: 1; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  document.body.appendChild(feedback);

  // Apply glitch effect to the container if available (respects reduced motion internally)
  const container = document.querySelector('.house-container') as HTMLElement;
  if (container) {
    glitch(container, { intensity: 6, duration: 0.3 });
  }

  // Remove feedback after animation
  setTimeout(() => {
    feedback.remove();
  }, 500);
}

/**
 * Update visual state based on discoveries
 */
function updateDiscoveryVisuals(container: HTMLElement): void {
  // Add discovered class to hidden triggers that have been found
  const hiddenTriggers = container.querySelectorAll('.house-hidden-trigger');

  hiddenTriggers.forEach((trigger) => {
    const target = trigger.getAttribute('data-hidden');
    if (target && discoveryState[target as keyof Pick<DiscoveryState, 'basement' | 'attic' | 'archive' | 'market' | 'crypt'>]) {
      trigger.classList.add('house-hidden-trigger--found');
    }
  });

  // Update status message based on discoveries
  if (discoveryState.totalDiscoveries > 0) {
    const statusEl = container.querySelector('.house-status');
    if (statusEl && discoveryState.totalDiscoveries === 5) {
      setTimeout(() => {
        statusEl.textContent = '> ALL ROOMS DISCOVERED // NOTHING IS HIDDEN';
      }, 10000);
    }
  }
}

/**
 * Map of trigger class names to their discovery method labels
 */
const TRIGGER_METHOD_NAMES: Record<string, string> = {
  'house-hidden-trigger--basement': 'ascii-author',
  'house-hidden-trigger--attic': 'ascii-videos',
  'house-hidden-trigger--archive': 'ascii-press',
  'house-hidden-trigger--market': 'ascii-buy',
  'house-hidden-trigger--crypt': 'ascii-guest',
};

/**
 * Setup invisible clickable areas for easter eggs
 */
function setupHiddenAreas(container: HTMLElement): void {
  const hiddenAreas = container.querySelectorAll('.house-hidden-trigger');

  hiddenAreas.forEach((area) => {
    area.addEventListener('click', () => {
      const target = area.getAttribute('data-hidden');
      if (target) {
        // Determine which trigger was clicked for method tracking
        const triggerClass = Array.from(area.classList).find(c => c.startsWith('house-hidden-trigger--'));
        const methodName = triggerClass ? (TRIGGER_METHOD_NAMES[triggerClass] || 'hidden-area') : 'hidden-area';

        recordDiscovery(target, 'click', methodName);
        showDiscoveryFeedback(target, 'click');
        setTimeout(() => {
          navigateTo(target);
        }, 500);
      }
    });

    // Add subtle mouse enter effect for atmosphere
    area.addEventListener('mouseenter', () => {
      // Very subtle hint that something is here
      const container = document.querySelector('.house-container');
      if (container && !prefersReducedMotion()) {
        container.classList.add('house-container--hint');
        setTimeout(() => {
          container.classList.remove('house-container--hint');
        }, 200);
      }
    });
  });

  // Add special click detection for empty spaces
  setupEmptySpaceDetection(container);
}

/**
 * Detect clicks in specific "empty" spaces
 */
function setupEmptySpaceDetection(container: HTMLElement): void {
  const floorPlan = container.querySelector('.house-floor-plan-wrapper');
  if (!floorPlan) return;

  // Track click patterns for multi-click easter eggs
  let clickPattern: { x: number; y: number; time: number }[] = [];

  floorPlan.addEventListener('click', (e: Event) => {
    const mouseEvent = e as MouseEvent;
    const rect = (floorPlan as HTMLElement).getBoundingClientRect();
    const x = (mouseEvent.clientX - rect.left) / rect.width;
    const y = (mouseEvent.clientY - rect.top) / rect.height;
    const now = Date.now();

    // Add click to pattern
    clickPattern.push({ x, y, time: now });

    // Remove old clicks (older than 3 seconds)
    clickPattern = clickPattern.filter((c) => now - c.time < 3000);

    // Check for triple-click in center area (secret basement access)
    const centerClicks = clickPattern.filter(
      (c) => c.x > 0.4 && c.x < 0.6 && c.y > 0.4 && c.y < 0.6
    );
    if (centerClicks.length >= 3) {
      recordDiscovery('basement', 'click', 'center-triple');
      showDiscoveryFeedback('basement', 'click');
      clickPattern = [];
      setTimeout(() => navigateTo('basement'), 500);
      return;
    }

    // Check for corner clicks (attic access)
    const topRightClicks = clickPattern.filter((c) => c.x > 0.8 && c.y < 0.2);
    if (topRightClicks.length >= 2) {
      recordDiscovery('attic', 'click', 'top-right-corner');
      showDiscoveryFeedback('attic', 'click');
      clickPattern = [];
      setTimeout(() => navigateTo('attic'), 500);
      return;
    }

    // Check for bottom-left double-click (archive access)
    const bottomLeftClicks = clickPattern.filter((c) => c.x < 0.2 && c.y > 0.8);
    if (bottomLeftClicks.length >= 2) {
      recordDiscovery('archive', 'click', 'bottom-left-corner');
      showDiscoveryFeedback('archive', 'click');
      clickPattern = [];
      setTimeout(() => navigateTo('archive'), 500);
    }
  });
}

/**
 * Setup idle timer - reveal easter egg after 60s of inactivity
 */
let idleTimeout: number | null = null;

function setupIdleTimer(): void {
  const resetIdle = () => {
    if (idleTimeout) {
      clearTimeout(idleTimeout);
    }
    idleTimeout = window.setTimeout(() => {
      // Subtle hint after 60 seconds of inactivity
      revealIdleHint();
    }, 60000);
  };

  ['mousemove', 'keydown', 'click', 'scroll'].forEach(event => {
    document.addEventListener(event, resetIdle);
  });

  resetIdle();
}

function revealIdleHint(): void {
  // Subtle visual hint about hidden content
  const hint = document.createElement('div');
  hint.className = 'house-idle-hint';
  const reducedMotion = prefersReducedMotion();
  hint.style.cssText = `
    position: fixed;
    bottom: 1rem;
    left: 1rem;
    color: var(--grey-light);
    font-size: var(--font-xs);
    opacity: ${reducedMotion ? '0.5' : '0'};
    ${reducedMotion ? '' : 'transition: opacity 2s;'}
  `;
  hint.textContent = '> there are rooms not shown on this map...';

  document.body.appendChild(hint);

  if (!reducedMotion) {
    setTimeout(() => {
      hint.style.opacity = '0.5';
    }, 100);
  }

  setTimeout(() => {
    if (!reducedMotion) {
      hint.style.opacity = '0';
      setTimeout(() => hint.remove(), 2000);
    } else {
      hint.remove();
    }
  }, 5000);
}

/**
 * Setup console easter eggs for view-source hunters
 */
function setupConsoleEasterEggs(): void {
  // Expose secret functions on window for console explorers
  (window as any).__chopras = {
    discover: (location: string) => {
      if (['basement', 'attic', 'archive', 'market', 'crypt'].includes(location)) {
        recordDiscovery(location, 'console', '__chopras.discover');
        showDiscoveryFeedback(location, 'console');
        setTimeout(() => navigateTo(location), 500);
        return `> ACCESSING ${location.toUpperCase()}...`;
      }
      return '> UNKNOWN LOCATION';
    },
    status: () => {
      const discovered = [
        discoveryState.basement && 'basement',
        discoveryState.attic && 'attic',
        discoveryState.archive && 'archive',
        discoveryState.market && 'market',
        discoveryState.crypt && 'crypt',
      ].filter(Boolean);
      return {
        discovered,
        remaining: 5 - discovered.length,
        methods: discoveryState.discoveryMethods,
        hint: discovered.length < 5 ? 'Try typing secret words...' : 'All rooms discovered!',
      };
    },
    reset: () => {
      localStorage.removeItem(DISCOVERY_STORAGE_KEY);
      discoveryState = {
        basement: false,
        attic: false,
        archive: false,
        market: false,
        crypt: false,
        discoveryMethods: {},
        firstVisit: Date.now(),
        totalDiscoveries: 0,
      };
      return '> MEMORY ERASED';
    },
    hint: () => {
      const hints = [
        'Have you tried typing "wesleyan"?',
        'The ASCII labels lead somewhere different than the buttons above.',
        'Some words unlock doors: vectors, manuscript, chopra...',
        'Wait long enough, and the house reveals itself.',
        'The map and the territory are not the same.',
        '↑↑↓↓←→←→ - a classic sequence.',
        'Press Escape three times.',
        'Click the ASCII room labels, not the buttons.',
        'Type "forbidden" to find the black market.',
        'Type "visitors" to enter the crypt.',
        'The buttons tell the truth. The ASCII lies beautifully.',
        'Not all rooms are drawn on the map.',
        'Some guests never leave.',
      ];
      return hints[Math.floor(Math.random() * hints.length)];
    },
  };
}

/**
 * Log view-source hints to console
 */
function logViewSourceHint(): void {
  // ASCII art header
  console.log(
    `%c
 ████████╗██╗  ██╗███████╗    ██╗  ██╗ ██████╗ ██╗   ██╗███████╗███████╗
 ╚══██╔══╝██║  ██║██╔════╝    ██║  ██║██╔═══██╗██║   ██║██╔════╝██╔════╝
    ██║   ███████║█████╗      ███████║██║   ██║██║   ██║███████╗█████╗
    ██║   ██╔══██║██╔══╝      ██╔══██║██║   ██║██║   ██║╚════██║██╔══╝
    ██║   ██║  ██║███████╗    ██║  ██║╚██████╔╝╚██████╔╝███████║███████╗
    ╚═╝   ╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚══════╝╚══════╝
                         OF CHOPRAS
`,
    'color: #8b0000; font-family: monospace'
  );

  console.log(
    '%c> You found the developer console. The manuscript appreciates your curiosity.',
    'color: #666; font-style: italic'
  );
  console.log(
    '%c> Try: __chopras.hint() for a clue, __chopras.status() for progress',
    'color: #444'
  );
  console.log(
    '%c> "There are rooms not shown on the map."',
    'color: #8b0000'
  );

  // HTML comment hints are added via index.html
}

/**
 * Get current discovery state (for external access)
 */
export function getDiscoveryState(): DiscoveryState {
  return { ...discoveryState };
}

/**
 * Check if all easter eggs have been discovered
 */
export function allDiscovered(): boolean {
  return discoveryState.basement && discoveryState.attic && discoveryState.archive && discoveryState.market && discoveryState.crypt;
}

/**
 * Cleanup house hub
 */
export function cleanupHouseHub(): void {
  if (idleTimeout) {
    clearTimeout(idleTimeout);
  }

  // Remove event listeners
  if (keydownHandler) {
    document.removeEventListener('keydown', keydownHandler);
    keydownHandler = null;
  }
  if (keySequenceHandler) {
    document.removeEventListener('keydown', keySequenceHandler);
    keySequenceHandler = null;
  }

  // Remove screen reader announcer if present
  const srAnnouncer = document.querySelector('.house-sr-announcer');
  if (srAnnouncer) {
    srAnnouncer.remove();
  }

  typedSequence = '';
  keySequence = [];
}
